/**
 * dia-reviews-popup.js
 *
 * Turns the Judge.me star badge into a control that opens this product's
 * reviews in a carousel: inline under the description (data-reviews-inline,
 * the product page) or in a modal (product-reviews-popup).
 *
 * Why this exists: the product page renders Judge.me's preview badge but not
 * the per-product review widget, so the stars had nothing to link to and did
 * not respond to a click. This moves the badge's click into the theme's own
 * popup-component and loads the reviews on demand.
 *
 * Reviews come from Judge.me's public widget endpoint (the one their widgets
 * call), so the theme never stores review copy of its own.
 */
(function () {
  'use strict';

  var REVIEWS_PER_PAGE = 12;
  var ENDPOINT = 'https://judge.me/reviews/reviews_for_widget';

  /** Cards shown per frame. Three only fits the desktop modal; below that the
      cards would be too narrow to read, so the carousel steps by fewer. */
  function perView() {
    if (window.matchMedia('(min-width: 990px)').matches) return 3;
    if (window.matchMedia('(min-width: 750px)').matches) return 2;
    return 1;
  }

  /** Judge.me renders the badge asynchronously, so wait for it to appear. */
  function whenBadgeReady(callback) {
    var existing = document.querySelector('.jdgm-prev-badge');
    if (existing) {
      callback(existing);
      return;
    }

    var observer = new MutationObserver(function () {
      var badge = document.querySelector('.jdgm-prev-badge');
      if (!badge) return;
      observer.disconnect();
      callback(badge);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Stop watching if the badge never arrives (app removed, blocked, no reviews).
    setTimeout(function () {
      observer.disconnect();
    }, 15000);
  }

  function buildUrl(shopDomain, productId, page) {
    return ENDPOINT
      + '?url=' + encodeURIComponent(shopDomain)
      + '&shop_domain=' + encodeURIComponent(shopDomain)
      + '&platform=shopify'
      + '&page=' + page
      + '&per_page=' + REVIEWS_PER_PAGE
      + '&product_id=' + encodeURIComponent(productId);
  }

  function stars(rating) {
    var filled = Math.round(Number(rating) || 0);
    var out = '';
    for (var i = 1; i <= 5; i++) {
      out += '<span class="jdgm-star ' + (i <= filled ? 'jdgm--on' : 'jdgm--off') + '"></span>';
    }
    return out;
  }

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  /** Judge.me returns author-written HTML; keep only the inline text markup. */
  function sanitizeBody(html) {
    var host = document.createElement('div');
    host.innerHTML = html || '';
    host.querySelectorAll('script, style, iframe, object, embed, link').forEach(function (node) {
      node.remove();
    });
    host.querySelectorAll('*').forEach(function (node) {
      [].slice.call(node.attributes).forEach(function (attr) {
        var name = attr.name.toLowerCase();
        if (name.indexOf('on') === 0 || name === 'srcdoc') node.removeAttribute(attr.name);
        if ((name === 'href' || name === 'src') && /^\s*javascript:/i.test(attr.value)) {
          node.removeAttribute(attr.name);
        }
      });
    });
    return host.innerHTML;
  }

  function initials(name) {
    var parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  }

  function reviewSlide(review, strings) {
    var photo = (review.pictures_urls || [])[0];
    var photoSrc = photo && (photo.huge || photo.original || photo.compact || photo.small);
    var name = review.reviewer_name || strings.anonymous;

    return ''
      + '<li class="reviews-popup__slide" role="group" aria-roledescription="review">'
      + '<article class="reviews-popup__card' + (photoSrc ? ' reviews-popup__card--has-photo' : '') + '">'
      + (photoSrc
        ? '<div class="reviews-popup__photo-wrap">'
          + '<img class="reviews-popup__photo" src="' + escapeHtml(photoSrc) + '" alt="" loading="lazy">'
          + '</div>'
        : '')
      + '<div class="reviews-popup__card-body">'
      + '<div class="reviews-popup__rating" aria-label="' + escapeHtml(review.rating + ' / 5') + '">'
      + stars(review.rating)
      + '</div>'
      + (review.title ? '<p class="reviews-popup__title">' + escapeHtml(review.title) + '</p>' : '')
      + '<div class="reviews-popup__text">' + sanitizeBody(review.body_html) + '</div>'
      + (review.reply_content
        ? '<div class="reviews-popup__reply"><span>' + escapeHtml(strings.reply) + '</span>'
          + '<div>' + sanitizeBody(review.reply_content) + '</div></div>'
        : '')
      + '<footer class="reviews-popup__meta">'
      + '<span class="reviews-popup__avatar" aria-hidden="true">' + escapeHtml(initials(name)) + '</span>'
      + '<span class="reviews-popup__who">'
      + '<span class="reviews-popup__author">' + escapeHtml(name) + '</span>'
      + '<span class="reviews-popup__sub">'
      + (review.verified_buyer
        ? '<span class="reviews-popup__verified">' + escapeHtml(strings.verified) + '</span>'
        : '')
      + '</span>'
      + '</span>'
      + '</footer>'
      + '</div>'
      + '</article>'
      + '</li>';
  }

  function ReviewsPopup(root) {
    this.root = root;
    this.body = root.querySelector('[data-reviews-body]');
    this.trigger = root.querySelector('[data-reviews-trigger]');
    this.productId = this.body.getAttribute('data-product-id');
    this.shopDomain = this.body.getAttribute('data-shop-domain');
    this.strings = {
      anonymous: this.body.getAttribute('data-string-anonymous'),
      verified: this.body.getAttribute('data-string-verified'),
      reply: this.body.getAttribute('data-string-reply'),
      empty: this.body.getAttribute('data-string-empty'),
      error: this.body.getAttribute('data-string-error'),
      previous: this.body.getAttribute('data-string-previous'),
      next: this.body.getAttribute('data-string-next'),
      counter: this.body.getAttribute('data-string-counter'),
      counterRange: this.body.getAttribute('data-string-counter-range')
    };
    this.page = 1;
    this.totalPages = 1;
    this.index = 0;
    this.reviews = [];
    this.loaded = false;
    this.loading = false;
    this.inline = root.hasAttribute('data-reviews-inline');
  }

  /** Inline, the carousel sits in the product info column, which is far narrower than the
      viewport on desktop, so the card count follows the column rather than the window. */
  ReviewsPopup.prototype.perView = function () {
    if (!this.inline) return perView();
    var width = this.body.clientWidth;
    if (width >= 900) return 3;
    if (width >= 520) return 2;
    return 1;
  };

  ReviewsPopup.prototype.labels = function () {
    return this.strings;
  };

  ReviewsPopup.prototype.setState = function (message) {
    this.body.innerHTML = '<p class="reviews-popup__state">' + escapeHtml(message) + '</p>';
  };

  ReviewsPopup.prototype.load = function (page) {
    var self = this;
    if (this.loading) return Promise.resolve();
    this.loading = true;

    return fetch(buildUrl(this.shopDomain, this.productId, page))
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        self.loading = false;
        self.loaded = true;
        self.total = data.number_of_reviews || 0;
        self.average = data.average_rating;
        self.totalPages = (data.pagination && data.pagination.total_pages) || 1;
        self.page = page;
        // Reviews with a photo lead: they are the most persuasive and the rarest,
        // so they should not be buried behind text-only ones. Order is otherwise
        // preserved, so Judge.me's own ranking still decides the rest.
        var reviews = (data.reviews || []).slice();
        var withPhoto = reviews.filter(function (r) { return (r.pictures_urls || []).length; });
        var withoutPhoto = reviews.filter(function (r) { return !(r.pictures_urls || []).length; });
        self.reviews = withPhoto.concat(withoutPhoto);
        self.index = 0;
        self.render();
      })
      .catch(function (error) {
        self.loading = false;
        console.warn('dia-reviews-popup: load failed', error);
        self.setState(self.labels().error);
      });
  };

  ReviewsPopup.prototype.render = function () {
    var labels = this.labels();

    if (!this.reviews.length) {
      this.setState(labels.empty);
      return;
    }

    var heading = ''
      + '<div class="reviews-popup__header">'
      + '<span class="reviews-popup__score">' + escapeHtml(this.average) + '</span>'
      + '<div class="reviews-popup__summary">'
      + '<span class="reviews-popup__summary-stars">' + stars(this.average) + '</span>'
      + '<span class="reviews-popup__summary-text">' + escapeHtml(String(this.total)) + '</span>'
      + '</div>'
      + '<p class="reviews-popup__product">' + escapeHtml(this.body.getAttribute('data-product-title')) + '</p>'
      + '</div>';

    var slides = '<ul class="reviews-popup__track" data-reviews-track>'
      + this.reviews.map(function (review) { return reviewSlide(review, labels); }).join('')
      + '</ul>';

    var nav = ''
      + '<div class="reviews-popup__nav">'
      + '<button type="button" class="reviews-popup__arrow" data-reviews-prev aria-label="' + escapeHtml(labels.previous) + '">'
      + '<span aria-hidden="true">&#8592;</span></button>'
      + '<span class="reviews-popup__counter" data-reviews-counter aria-live="polite"></span>'
      + '<button type="button" class="reviews-popup__arrow" data-reviews-next aria-label="' + escapeHtml(labels.next) + '">'
      + '<span aria-hidden="true">&#8594;</span></button>'
      + '</div>';

    this.body.innerHTML = heading + '<div class="reviews-popup__viewport">' + slides + '</div>' + nav;

    this.track = this.body.querySelector('[data-reviews-track]');
    this.counter = this.body.querySelector('[data-reviews-counter]');
    this.body.querySelector('[data-reviews-prev]').addEventListener('click', this.previous.bind(this));
    this.body.querySelector('[data-reviews-next]').addEventListener('click', this.next.bind(this));

    // Rotating a phone changes how many cards fit. Snap back to the start of the
    // frame the shopper was reading, so the row never lands mid-card.
    if (!this.resizeBound) {
      var self = this;
      this.resizeBound = function () {
        var visible = self.perView();
        self.index = Math.min(Math.floor(self.index / visible) * visible, self.lastIndex());
        self.update();
      };
      window.addEventListener('resize', this.resizeBound);
    }

    this.update();
  };

  /** Index of the last frame that still fills the viewport with cards. */
  ReviewsPopup.prototype.lastIndex = function () {
    return Math.max(0, this.reviews.length - this.perView());
  };

  ReviewsPopup.prototype.update = function () {
    if (!this.track) return;

    var visible = this.perView();
    this.index = Math.min(this.index, this.lastIndex());
    this.track.style.setProperty('--reviews-per-view', visible);
    this.track.style.transform = 'translateX(calc(' + (this.index * -100) + '% / ' + visible + '))';

    var first = (this.page - 1) * REVIEWS_PER_PAGE + this.index + 1;
    var last = Math.min(first + visible - 1, this.total);
    var labels = this.labels();
    this.counter.textContent = (visible > 1 && last > first ? labels.counterRange : labels.counter)
      .replace('{{ current }}', first)
      .replace('{{ last }}', last)
      .replace('{{ total }}', this.total);

    var atStart = this.index === 0 && this.page === 1;
    var atEnd = this.index >= this.lastIndex() && this.page >= this.totalPages;
    this.body.querySelector('[data-reviews-prev]').disabled = atStart;
    this.body.querySelector('[data-reviews-next]').disabled = atEnd;
  };

  ReviewsPopup.prototype.next = function () {
    if (this.index < this.lastIndex()) {
      this.index = Math.min(this.index + this.perView(), this.lastIndex());
      this.update();
    } else if (this.page < this.totalPages) {
      this.load(this.page + 1);
    }
  };

  ReviewsPopup.prototype.previous = function () {
    if (this.index > 0) {
      this.index = Math.max(this.index - this.perView(), 0);
      this.update();
    } else if (this.page > 1) {
      var self = this;
      this.load(this.page - 1).then(function () {
        self.index = self.lastIndex();
        self.update();
      });
    }
  };

  ReviewsPopup.prototype.init = function () {
    if (this.inline) {
      this.initInline();
      return;
    }

    var self = this;

    whenBadgeReady(function (badge) {
      badge.classList.add('reviews-popup__badge');
      badge.setAttribute('role', 'button');
      badge.setAttribute('tabindex', '0');

      function open(event) {
        event.preventDefault();
        if (!self.loaded) self.load(1);
        self.trigger.click();
      }

      badge.addEventListener('click', open);
      badge.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') open(event);
      });
    });
  };

  /** Inline mode: the reviews live in an accordion under the description. The badge opens
      that accordion and scrolls to it instead of opening a modal. */
  ReviewsPopup.prototype.initInline = function () {
    var self = this;
    var details = this.root.querySelector('[data-reviews-details]');
    var summary = this.root.querySelector('[data-reviews-summary]');

    // Reviews are fetched once the accordion nears the viewport, so they are usually in place
    // before it opens and the opening animation measures the full carousel, not the loading
    // line. The body has no width while closed, so the card count is recalculated on open.
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        if (!self.loaded) self.load(1);
      }, { rootMargin: '600px 0px' });
      observer.observe(this.root);
    }

    function onOpen() {
      if (!self.loaded) {
        self.load(1);
      } else {
        self.update();
      }
    }

    summary.addEventListener('click', function () {
      // The theme's collapsible sets `open` after this handler runs.
      setTimeout(function () {
        if (details.getAttribute('open') === 'true') onOpen();
      }, 0);
    });

    whenBadgeReady(function (badge) {
      badge.classList.add('reviews-popup__badge');
      badge.setAttribute('role', 'button');
      badge.setAttribute('tabindex', '0');
      badge.setAttribute('aria-controls', self.root.id);

      function open(event) {
        event.preventDefault();
        if (details.getAttribute('open') !== 'true') {
          summary.click();
        } else {
          onOpen();
        }
        var headerHeight = parseInt(
          getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
          10
        ) || 0;
        var top = self.root.getBoundingClientRect().top + window.pageYOffset - headerHeight - 16;
        window.scrollTo({ top: top, behavior: 'smooth' });
      }

      badge.addEventListener('click', open);
      badge.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') open(event);
      });
    });
  };

  function init() {
    document.querySelectorAll('[data-reviews-popup]').forEach(function (root) {
      try {
        new ReviewsPopup(root).init();
      } catch (error) {
        console.warn('dia-reviews-popup: init skipped', error);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
